import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import '../login/auth.css';
import './signup.css';
import { InstanceLogo } from '../../components/ui/InstanceLogo';
import { BRAND_NAME, INSTANCE_BANK_ID, INSTANCE_COUNTRY_ID } from '../../utils/appConfig';
import { CatalogFeature, featureIcon, formatMoney, formatTotals, totalOf } from '../features/featureUi';
import { normalizePanamaId, panamaIdError, panamaIdHasDv } from '../../utils/panamaId';

// Registro autogestionado de comercios: el comercio elige sus funcionalidades, completa los datos mínimos
// y al finalizar queda creado con su usuario administrador (llega por correo). Sin intervención de nadie.
// El país y el banco son los de la instancia (.env); sin banco (Swiftmart) el comercio queda sin pago con tarjeta.
// Es público: usa axios directo (topApi guarda el token de sesión de cada respuesta).
const publicApi = axios.create({ baseURL: process.env.REACT_APP_API_URL });

const COSTA_RICA = 1;
const PANAMA = 2;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface Option { id: number; name?: string; description?: string; format?: string | null; }

interface Catalog {
    countryId: number;
    idTypes: Option[];
    companyTypes: Option[];
    features: CatalogFeature[];
}

const STEPS = ['Plan', 'Empresa', 'Administrador'];

const emptyForm = {
    typeLegalId: '',
    documentNumber: '',
    dv: '',
    name: '',
    address: '',
    companyTypeId: '',
    activityCode: '',
    adminName: '',
    adminLastname: '',
    adminEmail: '',
    acceptTerms: false,
};

const apiError = (e: any) => {
    const data = e?.response?.data;
    const errors: string[] = data?.details?.errors ?? [];
    return [data?.error ?? 'No se pudo completar el registro. Intente de nuevo.', ...errors].join('\n');
};

export const SignupPage = () => {
    const navigate = useNavigate();
    const countryId = INSTANCE_COUNTRY_ID;
    const [catalog, setCatalog] = useState<Catalog | null>(null);
    const [loading, setLoading] = useState(true);
    const [step, setStep] = useState(0);
    const [selected, setSelected] = useState<string[]>([]);
    const [form, setForm] = useState(emptyForm);
    const [touched, setTouched] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [done, setDone] = useState<{ email: string; features: CatalogFeature[] } | null>(null);

    useEffect(() => {
        setLoading(true);
        setError('');
        // Lo que ofrece el banco de la instancia, con sus precios (sin banco: el catálogo del país)
        publicApi.get<Catalog>('/onboarding/catalog', { params: { countryId, bankId: INSTANCE_BANK_ID ?? undefined } })
            .then(resp => {
                setCatalog(resp.data);
                // Las funcionalidades base (retail) siempre van
                setSelected(prev => Array.from(new Set([
                    ...resp.data.features.filter(f => f.base).map(f => f.code),
                    ...prev.filter(code => resp.data.features.some(f => f.code === code)),
                ])));
            })
            .catch(e => setError(apiError(e)))
            .finally(() => setLoading(false));
    }, [countryId]);

    const features = catalog?.features ?? [];
    const chosen = useMemo(() => features.filter(f => selected.includes(f.code)), [features, selected]);
    const total = formatTotals(totalOf(chosen));
    const electronicInvoice = selected.includes('electronic-invoice');
    const idTypeFormat = catalog?.idTypes.find(t => String(t.id) === form.typeLegalId)?.format;
    // El extranjero (pasaporte) no tiene DV
    const hasDv = countryId === PANAMA && panamaIdHasDv(form.typeLegalId);

    const set = (field: keyof typeof emptyForm) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const value = e.target.type === 'checkbox' ? (e.target as HTMLInputElement).checked : e.target.value;
        setForm(prev => ({ ...prev, [field]: value }));
    };

    const toggle = (feature: CatalogFeature) => {
        if (feature.base) return;
        setSelected(prev => prev.includes(feature.code) ? prev.filter(c => c !== feature.code) : [...prev, feature.code]);
    };

    // Errores por paso (se muestran al intentar avanzar)
    const stepErrors = (index: number): Record<string, string> => {
        const errors: Record<string, string> = {};
        if (index === 1) {
            if (!form.typeLegalId) errors.typeLegalId = 'Seleccione el tipo de identificación.';
            if (!form.documentNumber.trim()) errors.documentNumber = 'Ingrese la identificación.';
            else if (countryId === PANAMA && form.typeLegalId) {
                const idError = panamaIdError(form.typeLegalId, form.documentNumber);
                if (idError) errors.documentNumber = idError;
                else if (!hasDv && electronicInvoice) errors.documentNumber = 'La factura electrónica requiere RUC o NT con DV. Quítela en el paso Plan o use otro tipo de identificación.';
            }
            if (hasDv && electronicInvoice && !/^\d{1,2}$/.test(form.dv.trim())) errors.dv = 'Ingrese el DV (1 o 2 dígitos) para la factura electrónica.';
            else if (hasDv && form.dv.trim() && !/^\d{1,2}$/.test(form.dv.trim())) errors.dv = 'El DV debe tener 1 o 2 dígitos.';
            if (!form.name.trim()) errors.name = 'Ingrese el nombre del comercio.';
            if (!form.address.trim()) errors.address = 'Ingrese la dirección.';
            if (!form.companyTypeId) errors.companyTypeId = 'Seleccione el tipo de comercio.';
            if (countryId === COSTA_RICA && !/^\d{6}$/.test(form.activityCode.trim())) errors.activityCode = 'Ingrese el código de actividad de Hacienda (6 dígitos).';
        }
        if (index === 2) {
            if (!form.adminName.trim()) errors.adminName = 'Ingrese su nombre.';
            if (!form.adminLastname.trim()) errors.adminLastname = 'Ingrese sus apellidos.';
            if (!EMAIL_REGEX.test(form.adminEmail.trim())) errors.adminEmail = 'Ingrese un correo válido.';
            if (!form.acceptTerms) errors.acceptTerms = 'Debe aceptar los términos y condiciones.';
        }
        return errors;
    };

    const errors = touched ? stepErrors(step) : {};
    const invalid = (field: string) => (errors[field] ? 'is-invalid' : '');
    const feedback = (field: string) => errors[field] && <div className="invalid-feedback">{errors[field]}</div>;

    const next = () => {
        if (Object.keys(stepErrors(step)).length) { setTouched(true); return; }
        setTouched(false);
        setStep(s => s + 1);
    };

    const back = () => { setTouched(false); setStep(s => s - 1); };

    const submit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (Object.keys(stepErrors(2)).length) { setTouched(true); return; }

        setSubmitting(true);
        setError('');
        try {
            const resp = await publicApi.post('/onboarding/register', {
                countryId,
                bankId: INSTANCE_BANK_ID ?? undefined,
                merchant: {
                    typeLegalId: Number(form.typeLegalId),
                    documentNumber: countryId === PANAMA ? normalizePanamaId(form.documentNumber) : form.documentNumber.trim(),
                    dv: hasDv ? form.dv.trim() || null : null,
                    name: form.name.trim(),
                    address: form.address.trim(),
                    companyTypeId: Number(form.companyTypeId),
                    activityCode: countryId === COSTA_RICA ? form.activityCode.trim() : null,
                },
                admin: { name: form.adminName.trim(), lastname: form.adminLastname.trim(), email: form.adminEmail.trim() },
                features: selected,
                acceptTerms: form.acceptTerms,
            });
            setDone({ email: resp.data?.admin?.email ?? form.adminEmail.trim(), features: chosen });
        } catch (err) {
            setError(apiError(err));
        } finally {
            setSubmitting(false);
        }
    };

    if (done) {
        return (
            <div className="auth-page">
                <main className="auth-main">
                    <div className="auth-card auth-card-wide text-center">
                        <InstanceLogo className="auth-logo mb-3" />
                        <div className="signup-done-icon mb-3"><i className="bi bi-check-lg" /></div>
                        <h4 className="mb-2">¡Su comercio está listo!</h4>
                        <p className="text-muted">
                            Enviamos a <strong>{done.email}</strong> su usuario administrador y una contraseña temporal.
                            Al ingresar por primera vez se le pedirá cambiarla.
                        </p>
                        <div className="d-flex flex-wrap justify-content-center gap-2 mb-4">
                            {done.features.map(f => (
                                <span key={f.code} className="badge rounded-pill bg-primary-subtle text-primary-emphasis">
                                    <i className={`bi ${featureIcon(f.code)} me-1`} />{f.name}
                                </span>
                            ))}
                        </div>
                        <button type="button" className="btn btn-primary w-100" onClick={() => navigate('/login', { replace: true })}>
                            <i className="bi bi-box-arrow-in-right me-1" /> Ir a iniciar sesión
                        </button>
                    </div>
                </main>
                <footer className="auth-footer">© {new Date().getFullYear()} {BRAND_NAME}</footer>
            </div>
        );
    }

    return (
        <div className="auth-page">
            <main className="auth-main">
                <div className="auth-card signup-card">
                    <div className="text-center mb-3">
                        <InstanceLogo className="auth-logo" />
                        <h4 className="mt-2 mb-1">Cree su comercio</h4>
                        <p className="text-muted mb-0 small">En tres pasos, sin trámites. Empiece a vender hoy mismo.</p>
                    </div>

                    {/* PASOS */}
                    <ol className="signup-steps" aria-label="Pasos del registro">
                        {STEPS.map((label, i) => (
                            <li key={label} className={i === step ? 'active' : i < step ? 'done' : ''} aria-current={i === step ? 'step' : undefined}>
                                <span className="signup-step-dot">{i < step ? <i className="bi bi-check" /> : i + 1}</span>
                                <span className="signup-step-label">{label}</span>
                            </li>
                        ))}
                    </ol>

                    {error && <div className="alert alert-danger small" style={{ whiteSpace: 'pre-line' }} role="alert">{error}</div>}

                    {loading || !catalog ? (
                        <div className="text-center py-5">
                            {!error && <span className="spinner-border text-primary" role="status" />}
                        </div>
                    ) : (
                        <form onSubmit={submit} noValidate>

                            {/* PASO 1: PLAN */}
                            {step === 0 && (<>
                                <p className="small text-muted mb-2">Seleccione las funcionalidades que necesita. Puede agregar más después.</p>
                                <div className="signup-features">
                                    {features.map(feature => {
                                        const on = selected.includes(feature.code);
                                        return (
                                            <button
                                                key={feature.code}
                                                type="button"
                                                className={`signup-feature ${on ? 'selected' : ''} ${feature.base ? 'base' : ''}`}
                                                onClick={() => toggle(feature)}
                                                aria-pressed={on}
                                                disabled={feature.base}
                                            >
                                                <span className="signup-feature-icon"><i className={`bi ${featureIcon(feature.code)}`} /></span>
                                                <span className="signup-feature-body">
                                                    <span className="signup-feature-name">
                                                        {feature.name}
                                                        {feature.base && <span className="badge bg-secondary-subtle text-secondary-emphasis ms-2">Incluido</span>}
                                                    </span>
                                                    {feature.description && <span className="signup-feature-desc">{feature.description}</span>}
                                                </span>
                                                <span className="signup-feature-price">
                                                    {formatMoney(feature.monthlyPrice, feature.currency)}
                                                    <small>/mes</small>
                                                </span>
                                                <i className={`bi ${on ? 'bi-check-circle-fill' : 'bi-circle'} signup-feature-check`} />
                                            </button>
                                        );
                                    })}
                                </div>
                            </>)}

                            {/* PASO 2: EMPRESA */}
                            {step === 1 && (
                                <div className="row g-3">
                                    <div className="col-12">
                                        <label htmlFor="name" className="form-label small text-muted mb-1">Nombre del comercio</label>
                                        <input id="name" className={`form-control ${invalid('name')}`} maxLength={100} value={form.name} onChange={set('name')} autoFocus />
                                        {feedback('name')}
                                    </div>
                                    <div className="col-sm-5">
                                        <label htmlFor="typeLegalId" className="form-label small text-muted mb-1">Tipo de identificación</label>
                                        <select id="typeLegalId" className={`form-select ${invalid('typeLegalId')}`} value={form.typeLegalId} onChange={set('typeLegalId')}>
                                            <option value="">Seleccione...</option>
                                            {catalog.idTypes.map(t => <option key={t.id} value={t.id}>{t.description}</option>)}
                                        </select>
                                        {feedback('typeLegalId')}
                                    </div>
                                    <div className={hasDv ? 'col-sm-5' : 'col-sm-7'}>
                                        <label htmlFor="documentNumber" className="form-label small text-muted mb-1">Identificación</label>
                                        <input id="documentNumber" className={`form-control ${invalid('documentNumber')}`} maxLength={50}
                                            placeholder={idTypeFormat ?? ''} value={form.documentNumber} onChange={set('documentNumber')} />
                                        {feedback('documentNumber')}
                                    </div>
                                    {hasDv && (
                                        <div className="col-sm-2">
                                            <label htmlFor="dv" className="form-label small text-muted mb-1">DV</label>
                                            <input id="dv" className={`form-control ${invalid('dv')}`} maxLength={2} inputMode="numeric" value={form.dv} onChange={set('dv')} />
                                            {feedback('dv')}
                                        </div>
                                    )}
                                    <div className="col-sm-6">
                                        <label htmlFor="companyTypeId" className="form-label small text-muted mb-1">Tipo de comercio</label>
                                        <select id="companyTypeId" className={`form-select ${invalid('companyTypeId')}`} value={form.companyTypeId} onChange={set('companyTypeId')}>
                                            <option value="">Seleccione...</option>
                                            {catalog.companyTypes.map(t => <option key={t.id} value={t.id}>{t.description}</option>)}
                                        </select>
                                        {feedback('companyTypeId')}
                                    </div>
                                    {countryId === COSTA_RICA && (
                                        <div className="col-sm-6">
                                            <label htmlFor="activityCode" className="form-label small text-muted mb-1">Código de actividad (Hacienda)</label>
                                            <input id="activityCode" className={`form-control ${invalid('activityCode')}`} maxLength={6} inputMode="numeric"
                                                value={form.activityCode} onChange={set('activityCode')} />
                                            {feedback('activityCode')}
                                        </div>
                                    )}
                                    <div className="col-12">
                                        <label htmlFor="address" className="form-label small text-muted mb-1">Dirección</label>
                                        <input id="address" className={`form-control ${invalid('address')}`} maxLength={250} value={form.address} onChange={set('address')} />
                                        {feedback('address')}
                                    </div>
                                </div>
                            )}

                            {/* PASO 3: ADMINISTRADOR */}
                            {step === 2 && (<>
                                <p className="small text-muted mb-3">Este será el usuario administrador del comercio. Le enviaremos el acceso a este correo.</p>
                                <div className="row g-3">
                                    <div className="col-sm-5">
                                        <label htmlFor="adminName" className="form-label small text-muted mb-1">Nombre</label>
                                        <input id="adminName" className={`form-control ${invalid('adminName')}`} maxLength={100} autoComplete="given-name"
                                            value={form.adminName} onChange={set('adminName')} autoFocus />
                                        {feedback('adminName')}
                                    </div>
                                    <div className="col-sm-7">
                                        <label htmlFor="adminLastname" className="form-label small text-muted mb-1">Apellidos</label>
                                        <input id="adminLastname" className={`form-control ${invalid('adminLastname')}`} maxLength={100} autoComplete="family-name"
                                            value={form.adminLastname} onChange={set('adminLastname')} />
                                        {feedback('adminLastname')}
                                    </div>
                                    <div className="col-12">
                                        <label htmlFor="adminEmail" className="form-label small text-muted mb-1">Correo electrónico</label>
                                        <input id="adminEmail" type="email" className={`form-control ${invalid('adminEmail')}`} maxLength={100} autoComplete="email"
                                            value={form.adminEmail} onChange={set('adminEmail')} />
                                        {feedback('adminEmail')}
                                    </div>
                                </div>

                                <div className="signup-summary mt-3">
                                    <div className="fw-semibold mb-1">{form.name}</div>
                                    <div className="d-flex flex-wrap gap-1 mb-2">
                                        {chosen.map(f => (
                                            <span key={f.code} className="badge rounded-pill bg-primary-subtle text-primary-emphasis">
                                                <i className={`bi ${featureIcon(f.code)} me-1`} />{f.name}
                                            </span>
                                        ))}
                                    </div>
                                    <div className="d-flex justify-content-between small">
                                        <span className="text-muted">Total mensual</span><strong>{total}</strong>
                                    </div>
                                </div>

                                <div className="form-check mt-3">
                                    <input id="acceptTerms" type="checkbox" className={`form-check-input ${invalid('acceptTerms')}`}
                                        checked={form.acceptTerms} onChange={set('acceptTerms')} />
                                    <label htmlFor="acceptTerms" className="form-check-label small">
                                        Acepto los <Link to="/terms-and-conditions" target="_blank">términos y condiciones</Link> y
                                        la <Link to="/privacy-policy" target="_blank">política de privacidad</Link>.
                                    </label>
                                    {feedback('acceptTerms')}
                                </div>
                            </>)}

                            {/* TOTAL Y NAVEGACIÓN */}
                            {step === 0 && (
                                <div className="d-flex justify-content-between align-items-center signup-total mt-3">
                                    <span className="text-muted small">Total mensual</span>
                                    <strong>{total}</strong>
                                </div>
                            )}

                            <div className="d-flex gap-2 mt-4">
                                {step > 0 && (
                                    <button type="button" className="btn btn-outline-secondary" onClick={back} disabled={submitting}>
                                        <i className="bi bi-arrow-left me-1" /> Atrás
                                    </button>
                                )}
                                {step < STEPS.length - 1 ? (
                                    // key distinto: si React reutilizara el botón, al cambiar a type="submit" en el mismo clic se enviaría el formulario
                                    <button key="next" type="button" className="btn btn-primary flex-grow-1" onClick={next}>
                                        Continuar <i className="bi bi-arrow-right ms-1" />
                                    </button>
                                ) : (
                                    <button key="submit" type="submit" className="btn btn-primary flex-grow-1" disabled={submitting}>
                                        {submitting
                                            ? <><span className="spinner-border spinner-border-sm me-2" />Creando su comercio...</>
                                            : <><i className="bi bi-check2-circle me-1" /> Finalizar</>}
                                    </button>
                                )}
                            </div>
                        </form>
                    )}

                    <div className="text-center mt-4 pt-3 border-top">
                        <span className="small text-muted">¿Ya tiene cuenta? </span>
                        <Link to="/login" className="small fw-semibold text-decoration-none">Inicie sesión</Link>
                    </div>
                </div>
            </main>
            <footer className="auth-footer">© {new Date().getFullYear()} {BRAND_NAME}</footer>
        </div>
    );
};

export default SignupPage;
